import type { Deal, DealStatus } from '../shared/deals';
import { FeedCard } from './FeedCard';

const CUTS = [
  { value: 0, label: 'All' },
  { value: 40, label: '40+' },
  { value: 60, label: '60+' },
  { value: 80, label: '80+' },
] as const;

const FEED_ACTIONS = [
  { label: 'Save', status: 'saved' as const },
  { label: 'Dismiss', status: 'dismissed' as const },
];

const SAVED_ACTIONS = [
  { label: 'Unsave', status: 'new' as const },
  { label: 'Dismiss', status: 'dismissed' as const },
];

export function Feed({
  deals,
  loading,
  minScore,
  onMinScore,
  onStatus,
  busyId,
}: {
  deals: Deal[];
  loading: boolean;
  minScore: number;
  onMinScore: (value: number) => void;
  onStatus: (deal: Deal, status: DealStatus) => void;
  busyId: string | null;
}) {
  return (
    <section className="px-5 py-8">
      <div className="mx-auto max-w-2xl">
        <div className="flex items-baseline justify-between gap-4">
          <p className="kicker">The Feed</p>
          <div className="flex gap-1.5">
            {CUTS.map((cut) => (
              <button
                key={cut.value}
                type="button"
                aria-pressed={cut.value === minScore}
                onClick={() => onMinScore(cut.value)}
                className={`border px-2 py-1 font-mono text-[0.6875rem] tracking-wide transition-colors ${
                  cut.value === minScore
                    ? 'border-ink bg-ink text-paper'
                    : 'border-rule text-ink-faint hover:border-rule-strong hover:text-ink'
                }`}
              >
                {cut.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rule-double mt-3" />

        {loading ? (
          <p className="py-10 text-center font-mono text-xs text-ink-faint">Reading the wire…</p>
        ) : deals.length === 0 ? (
          <div className="py-10">
            <h2 className="display-sm text-2xl">Nothing above the cut</h2>
            <p className="mt-2 max-w-prose text-sm leading-relaxed text-ink-soft">
              {minScore > 0
                ? 'Nothing scored high enough today. Drop the cut to see what else came in.'
                : 'The pipeline runs once a day. Anything it finds will be ranked here.'}
            </p>
          </div>
        ) : (
          <div>
            {deals.map((deal) => (
              <FeedCard
                key={deal.id}
                deal={deal}
                actions={FEED_ACTIONS}
                busy={busyId === deal.id}
                onStatus={(status) => onStatus(deal, status)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function SavedList({
  deals,
  loading,
  onStatus,
  busyId,
}: {
  deals: Deal[];
  loading: boolean;
  onStatus: (deal: Deal, status: DealStatus) => void;
  busyId: string | null;
}) {
  return (
    <section className="px-5 py-8">
      <div className="mx-auto max-w-2xl">
        <p className="kicker">Saved</p>
        <div className="rule-double mt-3" />

        {loading ? (
          <p className="py-10 text-center font-mono text-xs text-ink-faint">Pulling the file…</p>
        ) : deals.length === 0 ? (
          <div className="py-10">
            <h2 className="display-sm text-2xl">Your clippings file is empty</h2>
            <p className="mt-2 max-w-prose text-sm leading-relaxed text-ink-soft">
              Save anything from the feed and it will keep here.
            </p>
          </div>
        ) : (
          <div>
            {deals.map((deal) => (
              <FeedCard
                key={deal.id}
                deal={deal}
                actions={SAVED_ACTIONS}
                busy={busyId === deal.id}
                onStatus={(status) => onStatus(deal, status)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
